import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';

import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslatePipe } from '@ngx-translate/core';
import { StrutturaUser, UserProvider } from '@siat-fe/auth';
import { SelectFieldComponent } from '@siat-fe/shared';
import { filter, take, tap } from 'rxjs';

@Component({
  selector: 'lib-struttura-selection',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    SelectFieldComponent,
    MatFormFieldModule,
    TranslatePipe,
  ],
  template: `
    <h2 mat-dialog-title>
      {{ 'AUTH.SELECT_STRUTTURA.title' | translate }}
    </h2>

    <main mat-dialog-content>
      <p>{{ 'AUTH.SELECT_STRUTTURA.message' | translate }}</p>

      @if (strutturaForm) {
        <form [formGroup]="strutturaForm" class="mt-4">
          <siat-select-field
            [formGroup]="strutturaForm"
            [controlName]="'selectedStruttura'"
            [id]="'struttura'"
            [name]="'struttura'"
            [items]="struttureOptItems"
            [placeholder]="'AUTH.SELECT_STRUTTURA.placeholder' | translate"
            [label]="'AUTH.SELECT_STRUTTURA.placeholder' | translate"
          ></siat-select-field>
        </form>
      }
    </main>

    <div mat-dialog-actions class="flex justify-end space-x-2">
      <button
        type="submit"
        mat-button
        matButton="filled"
        color="primary"
        [disabled]="strutturaForm?.invalid"
        (click)="confirm()"
      >
        {{ 'CRUD.actions.confirm' | translate }}
      </button>
    </div>
  `,
})
export class StrutturaSelectionComponent implements OnInit {
  /**
   * The formgroup.
   */
  strutturaForm: FormGroup = new FormGroup({
    selectedStruttura: new FormControl<StrutturaUser | null>(null, [
      Validators.required,
    ]),
  });

  /**
   * The dialog ref.
   */
  dialogRef = inject(MatDialogRef<StrutturaSelectionComponent>);

  /**
   * The user provider.
   */
  userProvider = inject(UserProvider);

  /**
   * The available strutture.
   */
  struttureOptItems = [];

  /**
   * @inheritdoc
   */
  ngOnInit(): void {
    const availableStrutture = this.userProvider.userStruttureList;

    this.struttureOptItems = availableStrutture.map(
      (struttura: StrutturaUser) => {
        return {
          id: struttura?.id,
          name: struttura?.nome,
        };
      },
    );

    this.userProvider.currentUser$
      .pipe(
        filter((user) => !!user),
        take(1),
        tap(() => {
          const currentStruttura = this.userProvider.currentStruttura;
          const strutturaCtrl = this.strutturaForm.get('selectedStruttura');
          const findStruttura = this.struttureOptItems.find(
            (struttura) => struttura?.name === currentStruttura?.nome,
          );

          strutturaCtrl?.setValue(findStruttura);
        }),
      )
      .subscribe();
  }

  /**
   * Confirm the selection.
   */
  confirm(): void {
    const availableStrutture = this.userProvider.userStruttureList;
    const { selectedStruttura } = this.strutturaForm.value;
    const findStruttura = availableStrutture.find(
      (struttura) => struttura?.id === selectedStruttura?.id,
    );

    this.dialogRef.close(findStruttura);
  }
}
